import { isMobile } from '@farmasiet/frontendcore-reactcomponents';
import classNames from 'classnames';
import * as React from 'react';
import { useRef } from 'react';
import VisibilitySensor from 'react-visibility-sensor';

import { DiscountType } from '../../Blocks/DiscountType.csharp';
import { DiscountVariant } from '../../Blocks/DiscountVariant.csharp';
import { ProductDiscountViewModel } from '../../Blocks/ProductDiscount/ProductDiscountViewModel.csharp';
import { PriceViewModel } from '../../Catalog/Product/Price/PriceViewModel.csharp';
import { ProductAvailabilityMode } from '../../Components/Availability/ProductAvailabilityMode.csharp';
import { ProductBuyingMode } from '../../Components/Availability/ProductBuyingMode.csharp';
import { FavouriteProductButton } from '../../Customers/Favourites/Components/FavouritesButton/FavouriteProductButton';
import { ProductAvailabilityNotification } from '../../Customers/Notifications/ProductAvailability/Components/ProductAvailabilityNotification/ProductAvailabilityNotification';
import { ProductAvailabilityNotificationViewType } from '../../Customers/Notifications/ProductAvailability/Components/ProductAvailabilityNotification/ProductAvailabilityNotificationViewType.csharp';
import { addParameter, ExperimentsParamName, getExperimentAwareUrl, resolveExperiments } from '../../Experimentation/Components/ResolveExperimentId';
import { ExperimentationEventProductClickDetails } from '../../Experimentation/Models/ExperimentationEventProductClickDetails.csharp';
import { SearchQueryParamName } from '../../Filtering/Api/TrackEpiFindClick';
import { ProductSortingType } from '../../Filtering/Sorting/ProductSortingType.csharp';
import { AddToCartSection } from '../../Purchase/Cart/Modifications/AddItem/Components/AddToCart/AddToCart';
import { AddToCartMode } from '../../Purchase/Cart/Modifications/AddItem/ViewModel/AddToCartMode.csharp';
import { post } from '../../ReactComponents/API';
import { capitalizeFirstLetter } from '../../ReactComponents/CapitalizeFirstLetter';
import { ProductInfoLabel } from '../../ReactComponents/InfoLabel/ProductInfoLabel';
import { ProductTopLabel } from '../../ReactComponents/InfoLabel/ProductTopLabel';
import { PriceDisplay } from '../../ReactComponents/PriceDisplay';
import { ResponsivePicture } from '../../ReactComponents/ResponsivePicture/ResponsivePicture';
import { GoogleTagManager } from '../../Tracking/GoogleTagManager/GoogleTagManager';
import { handleClickEvents } from '../../Tracking/GoogleTagManager/HandleClickEvents';
import { ProductCardProps } from './ProductCardProps.csharp';
import { ProductCardVariant } from './ProductCardVariant.csharp';
import { ProductCardViewModel } from './ProductCardViewModel.csharp';
import { ProductRating } from './ProductRating';

interface ProductCardInterface extends ProductCardProps {
    disableQuantityPicker?: boolean;
}

export const ProductCard = (props: ProductCardInterface) => {
    const {
        nameInfo,
        link,
        rxUrl,
        price,
        promotions,
        availability,
        picture,
        code,
        subcategory,
        originName,
        promotionId,
        promotionName,
        showSucceededMessageAfterSave,
        minQuantity,
        maxQuantity,
        formConfiguration,
        isInPhysicalStoreMode,
        warningMessage,
        isAgeRestricted,
        isOnPrescription,
        showFixedLowPriceLabel,
        showNewProductLabel,
        powerReviewsRating,
        recommendationId,
        showPromoStar,
        showVeganLabel,
        showBestsellerLabel,
        showGoodPurchaseLabel,
        badgeSettings,
        isCooled,
        canBeCooled,
        isNewAddToCartEnabled
    } = props.model as ProductCardViewModel;

    const hidePowerReviews = props.hidePowerReviews;

    const variant = props.variant != null ? props.variant : ProductCardVariant.Vertical;
    const variantModifier = `ProductCard--${ProductCardVariant[variant]}`;
    const showOnlyFreeItemBadge = props.variant !== ProductCardVariant.Vertical;

    const productDiscountPromotions = promotions
        .filter((promo) => promo.promotionInfo.type == DiscountType.Product)
        .map((p) => p.promotionInfo as ProductDiscountViewModel);

    const [wasVisible, setWasVisible] = React.useState(false);
    const [productUrl, setProductUrl] = React.useState(link);
    const [addToCartUrl, setAddToCartUrl] = React.useState(props.cartItemUrl);
    const [experiments, setExperiments] = React.useState<string[]>([]);

    const productCardRef = useRef(null);

    React.useEffect(() => {
        const resolvedExperiments = resolveExperiments(productCardRef.current);

        let productUrl = `${link}`;
        let addToCartUrl = `${props.cartItemUrl}`;

        if (recommendationId) {
            productUrl = addParameter(productUrl, 'recommendationId', recommendationId);
            addToCartUrl = addParameter(addToCartUrl, 'recommendationId', recommendationId);
        }

        for (const experiment of resolvedExperiments) {
            productUrl = addParameter(productUrl, ExperimentsParamName, experiment);
        }

        if (props.model.optimizelySearchTrackingInfo?.searchQuery) {
            productUrl = addParameter(productUrl, SearchQueryParamName, props.model.optimizelySearchTrackingInfo.searchQuery);
        }

        setProductUrl(productUrl);
        setAddToCartUrl(addToCartUrl);
        setExperiments(resolvedExperiments);
    }, [recommendationId]);

    React.useEffect(() => {
        wasVisible && GoogleTagManager.productImpression(props.model);
    }, [wasVisible]);

    const renderPrice = (price: PriceViewModel, promotions: ProductDiscountViewModel[], variant: ProductCardVariant) => {
        const hasPromotions = promotions.length > 0;
        const applyStylesForDiscountedPrice = badgeSettings.hidePercentagePromotionBadge || variant !== ProductCardVariant.Vertical;
        return (
            <>
                {hasPromotions && (
                    <div className="ProductCard__ReducedPrice">
                        {renderOriginalPrice(price)}
                        {variant === ProductCardVariant.Vertical &&
                            promotions.map((promotion, i) => (
                                <span key={i} className="ProductCard__DiscountLabel">
                                    {promotion.value}
                                </span>
                            ))}
                    </div>
                )}
                <div
                    className={classNames(
                        'ProductCard__CurrentPrice',
                        hasPromotions && applyStylesForDiscountedPrice && 'ProductCard__CurrentPrice--Discounted'
                    )}
                    tabIndex={0}
                >
                    <PriceDisplay roundUp price={price.price} />
                </div>
            </>
        );
    };

    const renderPriceForMobile = (price: PriceViewModel, promotions: ProductDiscountViewModel[], variant: ProductCardVariant) => {
        return (
            <>
                {renderPrice(price, promotions, variant)}
                {renderButton()}
            </>
        );
    };

    const renderOriginalPrice = (price: PriceViewModel) => {
        return (
            price.originalPrice != price.price && (
                <span>
                    <span className="ProductCard__OriginalPrice">
                        <PriceDisplay roundUp price={price.originalPrice} />
                    </span>
                </span>
            )
        );
    };

    const renderBuyButton = () => {
        if (variant === ProductCardVariant.HorizontalSimple) return;

        const mode = resolveBuyingMode();

        return (
            <AddToCartSection
                cartItemUrl={addToCartUrl}
                validateItemUrl={props.validateItemUrl}
                isQuantityVisible={false}
                isProductPage={false}
                showSucceededMessageAfterSave={showSucceededMessageAfterSave}
                prescriptionUrl={rxUrl}
                experiments={experiments}
                position={props.model.position}
                epiFindClickTrackingUrl={props.epiFindClickTrackingUrl}
                searchTrackingInfo={props.model.optimizelySearchTrackingInfo}
                originName={originName}
                promotionId={promotionId}
                promotionName={promotionName}
                model={{
                    nameInfo: nameInfo,
                    image: picture,
                    code: code,
                    placedPrice: price.price,
                    profile: props.profile,
                    minQuantity: minQuantity,
                    maxQuantity: maxQuantity,
                    formConfiguration: formConfiguration,
                    warningMessage: warningMessage,
                    isAgeRestricted: isAgeRestricted,
                    mode: mode,
                    isOnPrescription: isOnPrescription,
                    subcategory: subcategory,
                    showRefrigeratedProductWarning: false,
                    isCooled,
                    canBeCooled
                }}
                disableQuantityPicker={props.disableQuantityPicker || !isNewAddToCartEnabled}
            />
        );
    };

    const resolveBuyingMode = () => {
        switch (availability.buyingMode) {
            case ProductBuyingMode.OnPrescription:
                return AddToCartMode.Prescription;
            case ProductBuyingMode.Active:
                if (props.model.isCooled && !props.model.areCooledProductsPurchasable) {
                    return AddToCartMode.NonPurchasableCooled;
                }
                return AddToCartMode.Default;
            default:
                if (availability.availabilityMode === ProductAvailabilityMode.PhysicalStoreOnly && isInPhysicalStoreMode) {
                    return AddToCartMode.Default;
                }

                return AddToCartMode.NotAvailable;
        }
    };

    const renderSubscribeForAvailabilityNotificationButton = () => (
        <ProductAvailabilityNotification
            viewType={ProductAvailabilityNotificationViewType.ProductCard}
            variantSku={code}
            checkEmailUrl={props.model.checkEmailUrl}
            url={props.model.productAvailabilityNotificationUrl}
        />
    );

    const getCropClassName = (): string => {
        let width: number;
        let height: number;

        if (picture.responsiveData.shouldUseFocusArea) {
            width = picture.responsiveData.focusArea.width;
            height = picture.responsiveData.focusArea.height;
        } else {
            width = picture.responsiveData.width;
            height = picture.responsiveData.height;
        }

        const ratio = width / height;

        return ratio >= 2 || ratio <= 0.5 ? 'ProductCard__Picture--CropWithSmallPaddings' : 'ProductCard__Picture--Crop';
    };

    const onLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        trackExperimentProductClick();
        GoogleTagManager.productCardClick(e, props.model, productUrl);
    };

    const trackProductClick = () => {
        trackExperimentProductClick();
    };

    const renderButton = () => {
        return (
            <div onClick={trackProductClick}>
                {availability.shouldAllowForAvailabilityNotificationSubscriptions ? renderSubscribeForAvailabilityNotificationButton() : renderBuyButton()}
            </div>
        );
    };

    const trackExperimentProductClick = () => {
        if (experiments.length > 0) {
            const url = getExperimentAwareUrl(props.experimentProductClickUrl, experiments);
            const promotions = props.model.promotions.map((promotion) => promotion.mainInfo);

            const eventDetails: ExperimentationEventProductClickDetails = {
                position: props.model.position,
                sku: props.model.code,
                promotionInfo: promotions.join(),
                sortingType: ProductSortingType[props.sortingType ?? -1]
            };

            post(url, eventDetails);
        }
    };

    return (
        <VisibilitySensor onChange={(isVisible) => isVisible && setWasVisible(true)} partialVisibility>
            <div ref={productCardRef} className={classNames('ProductCard', variantModifier, isInPhysicalStoreMode && 'ProductCard--PhysicalStoreMode')}>
                <a {...handleClickEvents(onLinkClick)} href={productUrl} className="ProductCard__Body">
                    <div className={classNames('ProductCard__Picture', getCropClassName())}>
                        {picture && <ResponsivePicture profile={props.profile} model={picture} showLoader={true} isLazyLoaded={props.isLazyLoading} />}
                        <ProductTopLabel
                            promotions={promotions}
                            showFixedLowPriceBadge={showFixedLowPriceLabel}
                            showNewProductLabel={showNewProductLabel}
                            variant={DiscountVariant.Small}
                            showPromoStar={showPromoStar}
                            showGoodPurchaseBadge={showGoodPurchaseLabel}
                            badgeSettings={badgeSettings}
                            showOnlyFreeItemBadge={showOnlyFreeItemBadge}
                        />
                    </div>
                    <div className="ProductCard__Content" aria-hidden={true}>
                        {variant === ProductCardVariant.Vertical && (
                            <ProductInfoLabel
                                showBestsellerLabel={showBestsellerLabel}
                                showNewItemLabel={showNewProductLabel}
                                showVeganProductLabel={showVeganLabel}
                                variant={DiscountVariant.Small}
                                badgeSettings={badgeSettings}
                                numberOfLabels={2}
                            />
                        )}
                        {variant !== ProductCardVariant.CartHorizontal ? (
                            <>
                                {nameInfo.mainName && <div className="ProductCard__Heading">{nameInfo.mainName}</div>}
                                <h2 className="ProductCard__Subheading">
                                    <span>{capitalizeFirstLetter(nameInfo.productHeader2)},&nbsp;</span>
                                    <div className="ProductCard__VariantSubheader">
                                        <span>{capitalizeFirstLetter(nameInfo.variantSubheader)}</span>
                                    </div>
                                </h2>
                            </>
                        ) : (
                            <>
                                {nameInfo.mainName && <h2 className="ProductCard__Heading">{nameInfo.mainName}</h2>}
                                <h3 className="ProductCard__Subheading">
                                    <span>{capitalizeFirstLetter(nameInfo.productHeader2)},&nbsp;</span>
                                    <div className="ProductCard__VariantSubheader">
                                        <span>{capitalizeFirstLetter(nameInfo.variantSubheader)}</span>
                                    </div>
                                </h3>{' '}
                            </>
                        )}

                        {/*{props.model.searchTrackingInfo?.score && <h3>{props.model.searchTrackingInfo.score}</h3>}*/}
                    </div>
                </a>
                {props.showFavouritesButton && props.favouritesUrl && !isOnPrescription && (
                    <div className="ProductCard__FavouritesButton">
                        <FavouriteProductButton favouritesUrl={props.favouritesUrl} product={props.model} />
                    </div>
                )}
                <div className="ProductCard__Footer">
                    {!hidePowerReviews && variant === ProductCardVariant.Vertical && (
                        <div className="ProductCard__ReviewsSnippet">
                            <ProductRating rating={powerReviewsRating} />
                        </div>
                    )}
                    {isMobile() && variant === ProductCardVariant.HorizontalCompact ? (
                        <div className="ProductCard__Price">{renderPriceForMobile(price, productDiscountPromotions, variant)}</div>
                    ) : (
                        <>
                            <div className="ProductCard__Price">{renderPrice(price, productDiscountPromotions, variant)}</div>
                            {renderButton()}
                        </>
                    )}
                </div>
            </div>
        </VisibilitySensor>
    );
};
